import { motion } from "framer-motion";
import type { IconType } from "react-icons";
import { LuSearch, LuLock, LuRocket, LuHeartHandshake } from "react-icons/lu";

const COMMITMENTS: { icon: IconType; title: string; desc: string; accent: string }[] = [
  {
    icon: LuSearch,
    title: "Transparent AI",
    desc: "We believe investors should understand how recommendations are generated. Our AI provides clear reasoning behind every insight — so you always know why, not just what.",
    accent: "blue",
  },
  {
    icon: LuLock,
    title: "Data Security & Privacy",
    desc: "Your financial data is protected with bank-level encryption. We never sell or share individual investor data. SOC 2 Type II audit nearly complete.",
    accent: "emerald",
  },
  {
    icon: LuRocket,
    title: "Continuous Innovation",
    desc: "Real estate markets evolve, and so does our AI. We ship improvements weekly based on real investor feedback — not a roadmap planned two years ago.",
    accent: "indigo",
  },
  {
    icon: LuHeartHandshake,
    title: "Your Success First",
    desc: "Every feature must answer one question: does it help investors achieve better returns with less effort? If not, we don't build it.",
    accent: "cyan",
  },
];

const accentMap: Record<string, string> = {
  blue:   "dark:bg-blue-500/10 bg-blue-100 dark:border-blue-500/20 border-blue-200",
  emerald:"dark:bg-emerald-500/10 bg-emerald-100 dark:border-emerald-500/20 border-emerald-200",
  indigo: "dark:bg-indigo-500/10 bg-indigo-100 dark:border-indigo-500/20 border-indigo-200",
  cyan:   "dark:bg-cyan-500/10 bg-cyan-100 dark:border-cyan-500/20 border-cyan-200",
};

const iconColorMap: Record<string, string> = {
  blue:   "text-blue-600 dark:text-blue-300",
  emerald:"text-emerald-600 dark:text-emerald-300",
  indigo: "text-indigo-600 dark:text-indigo-300",
  cyan:   "text-cyan-600 dark:text-cyan-300",
};

export function CommitmentSection() {
  return (
    <section className="px-4 sm:px-6 py-20 mx-auto max-w-[1100px]">
      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
          Our Commitment
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-3">
          What we promise{" "}
          <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            every investor
          </span>
        </h2>
        <p className="text-base dark:text-slate-400 text-gray-500 max-w-xl mx-auto">
          These aren't marketing bullet points. They're the standards we hold ourselves to — every day.
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {COMMITMENTS.map((item, i) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className="flex gap-5 dark:bg-white/[0.04] bg-white border dark:border-white/[0.08] border-gray-200 rounded-2xl p-6 hover:dark:bg-white/[0.07] hover:border-blue-200 hover:shadow-md transition-all duration-300 group"
          >
            {/* Icon */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center ${accentMap[item.accent]} group-hover:scale-110 transition-transform duration-300`}>
              <item.icon className={`w-6 h-6 ${iconColorMap[item.accent]}`} />
            </div>
            {/* Text */}
            <div>
              <h3 className="text-base font-bold dark:text-white text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm dark:text-slate-400 text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bottom CTA strip */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="mt-12 dark:bg-gradient-to-r dark:from-vw-highlight/80 dark:to-vw-highlight/40 bg-blue-600 rounded-2xl p-8 text-center"
      >
        <h3 className="text-xl font-extrabold text-white mb-2">
          Ready to invest with intelligence?
        </h3>
        <p className="text-sm text-blue-100 mb-6">
          14-day free trial · Cancel anytime · No credit card friction
        </p>
        <a href="https://app.vawena.ai/signup" target="_self" rel="noopener noreferrer">
          <button className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-white text-blue-700 font-bold text-sm hover:bg-blue-50 transition-all duration-200 hover:scale-[1.03]">
            Start Free Trial
          </button>
        </a>
      </motion.div>
    </section>
  );
}
